import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import PRODUCT_DATA from "../data/products";
import { slugify, getAllProducts } from "../utils/helpers";
import { handleImageError } from "../utils/imageFallback";
import "./MobileMenu.css";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function MobileMenu({ isOpen, onClose, currentSlug }) {
  const navigate = useNavigate();
  const categories = Object.keys(PRODUCT_DATA);

  const [search, setSearch] = useState("");
  const dialogRef = useRef(null);
  const overlayRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  /* Flattening 272 products is not free — do it once, not on every keystroke. */
  const products = useMemo(() => getAllProducts(PRODUCT_DATA), []);

  const trimmed = search.trim();

  const results = useMemo(() => {
    if (!trimmed) return [];
    const q = trimmed.toLowerCase();
    return products
      .filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.brand?.toLowerCase().includes(q)
      )
      .slice(0, 6);
  }, [trimmed, products]);

  /* Close on Escape, and stop the page behind the drawer from scrolling. */
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  /* Never leave a stale query behind when the drawer reopens. */
  useEffect(() => {
    if (!isOpen) setSearch("");
  }, [isOpen]);

  /* Move focus into the dialog, trap Tab within it, inert the rest of the
     page while open, and hand focus back to whatever opened the menu. */
  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedRef.current = document.activeElement;
    const dialog = dialogRef.current;
    const firstFocusable = dialog?.querySelector(FOCUSABLE_SELECTOR);
    (firstFocusable ?? dialog)?.focus();

    const onKeyDown = (e) => {
      if (e.key !== "Tab" || !dialog) return;
      const nodes = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    dialog?.addEventListener("keydown", onKeyDown);

    const appEl = document.querySelector(".app");
    const overlay = overlayRef.current;
    const inertedSiblings = appEl
      ? Array.from(appEl.children).filter(
          (el) => el !== dialog && el !== overlay
        )
      : [];
    inertedSiblings.forEach((el) => el.setAttribute("inert", ""));

    return () => {
      dialog?.removeEventListener("keydown", onKeyDown);
      inertedSiblings.forEach((el) => el.removeAttribute("inert"));
      previouslyFocusedRef.current?.focus?.();
    };
  }, [isOpen]);

  const openProduct = (product) => {
    navigate(`/category/${slugify(product.category)}`);
    setSearch("");
    onClose();
  };

  return (
    <>
      <div
        ref={overlayRef}
        className={`mobile-overlay ${isOpen ? "open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={dialogRef}
        className={`mobile-menu ${isOpen ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        aria-hidden={!isOpen}
        tabIndex={-1}
        {...(!isOpen && { inert: "" })}
      >
        <div className="mobile-menu-header">
          <h3>Sai Concept</h3>
          <button className="mobile-close" type="button" onClick={onClose} aria-label="Close menu">
            ✕
          </button>
        </div>

        <div className="mobile-search">
          <input
            type="search"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search products"
          />

          {trimmed && (
            <div className="mobile-search-results">
              {results.length === 0 ? (
                <div className="mobile-search-empty">No matches.</div>
              ) : (
                results.map((p, i) => (
                  <button
                    key={`${p.category}-${p.name}-${i}`}
                    type="button"
                    className="mobile-search-item"
                    onClick={() => openProduct(p)}
                  >
                    {p.img && (
                      <img
                        src={p.img}
                        alt=""
                        className="mobile-search-img"
                        loading="lazy"
                        onError={handleImageError}
                      />
                    )}
                    <span>{p.name}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <Link
          to="/"
          className={`mobile-nav-item ${currentSlug === "home" ? "active" : ""}`}
          onClick={onClose}
        >
          🏠 Home
        </Link>

        {categories.map((cat) => (
          <Link
            key={cat}
            to={`/category/${slugify(cat)}`}
            className={`mobile-nav-item ${
              currentSlug === slugify(cat) ? "active" : ""
            }`}
            onClick={onClose}
          >
            {PRODUCT_DATA[cat].icon} {cat}
          </Link>
        ))}
      </div>
    </>
  );
}
